import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { loginUser } from "../services/authService";
import { APIClient, initializeApiClient } from "../utils/api/apiClient";
import { getLocation } from "../utils/Common/location";

type AppRole = "admin" | "gc" | "sub" | "lender" | "owner" | "inspector";

const ROLE_REDIRECT: Record<AppRole, string> = {
  admin: "/admin",
  gc: "/gc-dashboard",
  sub: "/lien-waiver",
  lender: "/bank-dashboard",
  owner: "/owner-dashboard",
  inspector: "/project-inspection",
};

interface ApiError {
  message?: string;
  raw?: {
    fields?: Record<string, string>;
  };
}

interface LoginTokens {
  accessToken: string;
  refreshToken: string;
}

interface LoginUser {
  roles?: string[];
  [key: string]: unknown;
}

interface LoginResponseData {
  data: {
    requires2fa: boolean;
    twoFactorToken?: string;
    user?: LoginUser;
    tokens?: LoginTokens;
  };
}

const useLogin = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const handleLogin = async (
    email: string,
    password: string,
    selectedRole?: string,
  ): Promise<void> => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }

    setLoading(true);
    setError("");

    const location = await getLocation();

    const { data, error: apiError } = (await loginUser(
      email,
      password,
      location,
      selectedRole,
    )) as {
      data: LoginResponseData | null;
      error: ApiError | null;
    };

    if (apiError) {
      const fields = apiError.raw?.fields;
      const fieldError = fields ? Object.values(fields)[0] : null;
      setError(
        fieldError || apiError.message || "Login failed. Please try again.",
      );
      setLoading(false);
      return;
    }

    const { requires2fa, twoFactorToken, user, tokens } = data!.data;

    if (requires2fa) {
      setLoading(false);
      navigate("/two-factor-auth", {
        state: { twoFactorToken, selectedRole },
        replace: true,
      });
      return;
    }

    // Multi-role users: sign in as the role picked at login, not just the first one.
    const roles = user!.roles ?? [];
    const role = (
      selectedRole && roles.includes(selectedRole) ? selectedRole : (roles[0] ?? "")
    ) as AppRole;

    localStorage.setItem("token", tokens!.accessToken);
    localStorage.setItem("refreshToken", tokens!.refreshToken);
    localStorage.setItem("role", role);
    localStorage.setItem("user", JSON.stringify({ ...user, role }));
    localStorage.setItem("authChanged", Date.now().toString());

    APIClient.setApiToken(tokens!.accessToken);
    APIClient.setRole(role);
    initializeApiClient(role);

    window.dispatchEvent(new CustomEvent("auth-ready"));

    const redirectTo = sessionStorage.getItem("redirect_after_login");
    sessionStorage.removeItem("redirect_after_login");
    navigate(redirectTo || ROLE_REDIRECT[role] || "/", { replace: true });
  };

  return { handleLogin, loading, error };
};

export default useLogin;
